import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ExternalLink,
  Trophy,
  GripVertical,
} from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  getCoachingAthlete,
  listPrograms,
  getProgram,
  updateProgram,
} from "../../lib/api";
import {
  workoutTypeLabel,
  workoutTypeColor,
  formatSegmentValue,
  strengthBlockLabel,
  weekVolume,
  formatVolume,
} from "../../lib/coaching";
import { computeVdot, trainingPaces } from "../../lib/vdot";
import { toast } from "sonner";

const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Find the Monday of the week containing the given date (UTC-safe).
function mondayOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  date.setHours(0, 0, 0, 0);
  return date;
}

function isoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmt(d) {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Given a program with `start_date`, find the week_number that covers `targetDateISO`.
 * Returns null if outside the program's range.
 */
function programWeekForDate(program, targetDateISO) {
  if (!program.start_date) return null;
  const start = new Date(program.start_date + "T00:00:00");
  const target = new Date(targetDateISO + "T00:00:00");
  const days = Math.floor((target - start) / (1000 * 60 * 60 * 24));
  if (days < 0) return null;
  const weekNum = Math.floor(days / 7) + 1;
  if (weekNum > (program.weeks?.length || 0)) return null;
  return program.weeks.find((w) => w.week_number === weekNum) || null;
}

/**
 * Given a week.days[] (length 7, Mon..Sun) and a Monday-anchored date, find the day for `targetDateISO`.
 */
function dayForDate(week, weekStartDate, targetDateISO) {
  if (!week) return null;
  const start = new Date(weekStartDate);
  const target = new Date(targetDateISO + "T00:00:00");
  const offset = Math.round((target - start) / (1000 * 60 * 60 * 24));
  if (offset < 0 || offset > 6) return null;
  return week.days?.[offset] || null;
}

function WorkoutChip({ workout, programTitle, programId, weekNumber, dragHandleProps, isDragging }) {
  const color = workoutTypeColor(workout.type);
  const label = workout.title?.trim() || workoutTypeLabel(workout.type);
  return (
    <div
      className={`border bg-white pl-1 pr-2 py-1.5 transition-colors group flex items-stretch gap-1 ${
        isDragging
          ? "border-[var(--tir-burgundy)] shadow-lg"
          : "border-[var(--tir-border)] hover:border-[var(--tir-burgundy)]"
      }`}
      style={{ borderLeftColor: color, borderLeftWidth: 3 }}
      data-testid={`combined-workout-${workout.id}`}
    >
      <div
        {...(dragHandleProps || {})}
        className="flex items-center text-[var(--tir-muted)]/40 hover:text-[var(--tir-burgundy)] cursor-grab active:cursor-grabbing -ml-0.5 pr-0.5"
        title="Drag to reschedule"
        aria-label="Drag to reschedule"
        data-testid={`combined-workout-drag-${workout.id}`}
      >
        <GripVertical size={11} />
      </div>
      <Link
        to={`/coaching/programs/${programId}/edit?week=${weekNumber}`}
        className="flex-1 min-w-0 block"
      >
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="text-[10px] font-semibold tracking-[0.1em] uppercase text-[var(--tir-ink)] truncate">
            {label}
          </div>
          <ExternalLink size={9} className="text-[var(--tir-muted)] opacity-0 group-hover:opacity-100 shrink-0" />
        </div>
        {workout.duration_or_distance && (
          <div className="font-mono-tir text-[10px] text-[var(--tir-ink)]">
            {workout.duration_or_distance}
          </div>
        )}
        {(workout.segments || []).length > 0 && (
          <div className="text-[9px] text-[var(--tir-muted)] mt-0.5 truncate">
            {workout.segments
              .slice(0, 2)
              .map((s) => formatSegmentValue(s))
              .filter(Boolean)
              .join(" · ")}
            {workout.segments.length > 2 ? " · …" : ""}
          </div>
        )}
        {(workout.strength_blocks || []).length > 0 && (
          <div className="text-[9px] text-[var(--tir-muted)] mt-0.5 truncate">
            {workout.strength_blocks
              .slice(0, 2)
              .map((b) => `${strengthBlockLabel(b.kind)} (${(b.exercises || []).length})`)
              .join(" · ")}
          </div>
        )}
        <div className="text-[9px] tracking-[0.14em] uppercase text-[var(--tir-burgundy)] font-semibold mt-1 truncate">
          {programTitle}
        </div>
      </Link>
    </div>
  );
}

export default function CoachingCalendarPage() {
  const { id: athleteId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [programs, setPrograms] = useState([]); // full programs (with weeks)
  const [loading, setLoading] = useState(true);
  const [anchorDate, setAnchorDate] = useState(() => mondayOfWeek(new Date()));

  useEffect(() => {
    (async () => {
      try {
        const [a, list] = await Promise.all([
          getCoachingAthlete(athleteId),
          listPrograms({ athlete_id: athleteId }),
        ]);
        setAthlete(a);
        // Fetch each full program (list endpoint returns weeks too, but be safe)
        const fulls = await Promise.all(
          (list || []).map((p) => getProgram(p.id).catch(() => null))
        );
        setPrograms(fulls.filter(Boolean));
      } catch {
        toast.error("Couldn't load combined calendar");
        navigate("/coaching");
      } finally {
        setLoading(false);
      }
    })();
  }, [athleteId, navigate]);

  const weekDates = useMemo(() => {
    const start = new Date(anchorDate);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [anchorDate]);

  // For each program, compute which of its weeks falls into the current calendar window
  const programWeeksInView = useMemo(() => {
    const map = new Map();
    for (const p of programs) {
      const target = isoDate(weekDates[0]);
      const wk = programWeekForDate(p, target);
      if (wk) {
        // Compute the program's own week-start ISO
        const startProg = new Date(p.start_date + "T00:00:00");
        const wkStart = new Date(startProg);
        wkStart.setDate(startProg.getDate() + (wk.week_number - 1) * 7);
        map.set(p.id, { program: p, week: wk, wkStart });
      }
    }
    return map;
  }, [programs, weekDates]);

  const athletePaces = useMemo(() => {
    const pp = athlete?.pace_profile;
    if (!pp) return null;
    const vdot = computeVdot(pp.race_distance_m, pp.race_time_seconds);
    return trainingPaces(vdot);
  }, [athlete?.pace_profile]);

  // Aggregate workouts by day
  const workoutsByDay = useMemo(() => {
    const buckets = weekDates.map(() => []);
    for (const { program, week, wkStart } of programWeeksInView.values()) {
      for (let i = 0; i < 7; i++) {
        const targetISO = isoDate(weekDates[i]);
        const day = dayForDate(week, wkStart, targetISO);
        if (day && (day.workouts || []).length > 0) {
          for (const w of day.workouts) {
            buckets[i].push({
              workout: w,
              programId: program.id,
              programTitle: program.title,
              weekNumber: week.week_number,
            });
          }
        }
      }
    }
    return buckets;
  }, [programWeeksInView, weekDates]);

  // Races landing in this calendar week
  const racesInView = useMemo(() => {
    const list = athlete?.races || [];
    const out = [];
    for (const r of list) {
      if (!r.date) continue;
      const idx = weekDates.findIndex((d) => isoDate(d) === r.date);
      if (idx >= 0) out.push({ race: r, dayIdx: idx });
    }
    return out;
  }, [athlete?.races, weekDates]);

  // Combined volume from all programs for the week-in-view
  const combinedVolume = useMemo(() => {
    let km = 0;
    let minutes = 0;
    for (const { week } of programWeeksInView.values()) {
      const v = weekVolume(week);
      km += v.km;
      minutes += v.minutes;
    }
    return { km, minutes };
  }, [programWeeksInView]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]">
        Loading combined calendar…
      </div>
    );
  }
  if (!athlete) return null;

  const goPrev = () => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() - 7);
    setAnchorDate(d);
  };
  const goNext = () => {
    const d = new Date(anchorDate);
    d.setDate(d.getDate() + 7);
    setAnchorDate(d);
  };
  const goToday = () => setAnchorDate(mondayOfWeek(new Date()));

  // Persist the move back to the source program. Only the program that owns
  // the dragged workout is updated — other programs are left untouched.
  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    )
      return;

    const sourceDayIdx = parseInt(source.droppableId.replace("day-", ""), 10);
    const destDayIdx = parseInt(destination.droppableId.replace("day-", ""), 10);
    const sepIdx = draggableId.indexOf("::");
    const programId = draggableId.slice(0, sepIdx);
    const workoutId = draggableId.slice(sepIdx + 2);

    const data = programWeeksInView.get(programId);
    if (!data) return;
    const { program, week } = data;

    // Clone day arrays so we can mutate locally
    const newDays = (week.days || []).map((d) => ({
      ...d,
      workouts: [...(d.workouts || [])],
    }));
    const sourceDay = newDays[sourceDayIdx];
    const destDay = newDays[destDayIdx];
    if (!sourceDay || !destDay) return;

    const localSrcIdx = sourceDay.workouts.findIndex((w) => w.id === workoutId);
    if (localSrcIdx < 0) return;
    const [moved] = sourceDay.workouts.splice(localSrcIdx, 1);

    // Translate destination.index (computed against the merged bucket — which
    // can include workouts from sibling programs) into a position within the
    // destination day's own workouts list. We count how many same-program
    // workouts appear before destination.index in the merged bucket.
    const merged = workoutsByDay[destDayIdx];
    let localDestIdx = 0;
    for (let i = 0; i < destination.index && i < merged.length; i++) {
      const entry = merged[i];
      if (
        sourceDayIdx === destDayIdx &&
        entry.programId === programId &&
        entry.workout.id === workoutId
      ) {
        // Skip the position the moved item used to occupy
        continue;
      }
      if (entry.programId === programId) localDestIdx++;
    }
    destDay.workouts.splice(localDestIdx, 0, moved);

    // Optimistic local update
    const newWeek = { ...week, days: newDays };
    const newProgram = {
      ...program,
      weeks: program.weeks.map((w) =>
        w.week_number === newWeek.week_number ? newWeek : w
      ),
    };
    setPrograms((prev) =>
      prev.map((p) => (p.id === programId ? newProgram : p))
    );

    // Persist
    (async () => {
      try {
        await updateProgram(programId, {
          title: newProgram.title,
          goal: newProgram.goal,
          target_race: newProgram.target_race,
          experience_level: newProgram.experience_level,
          max_mileage: newProgram.max_mileage,
          prerequisite: newProgram.prerequisite,
          total_weeks: newProgram.total_weeks,
          start_day: newProgram.start_day,
          start_date: newProgram.start_date,
          show_total_volume: newProgram.show_total_volume,
          intro_howto: newProgram.intro_howto,
          intro_modify: newProgram.intro_modify,
          weeks: newProgram.weeks,
        });
        if (sourceDayIdx !== destDayIdx) {
          const label = moved.title?.trim() || workoutTypeLabel(moved.type);
          toast.success(
            `Moved "${label}" → ${DAY_LABELS[destDayIdx]} in ${program.title}`
          );
        }
      } catch {
        toast.error("Couldn't save move — reverting");
        try {
          const fresh = await getProgram(programId);
          setPrograms((prev) => prev.map((p) => (p.id === programId ? fresh : p)));
        } catch {
          /* ignore */
        }
      }
    })();
  };

  return (
    <div
      className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-10 py-6"
      data-testid="combined-calendar-page"
    >
      <Link
        to={`/coaching/athletes/${athleteId}`}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-3"
      >
        <ArrowLeft size={14} /> {athlete.name}
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-5 mb-6 flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
            Coaching · Combined calendar
          </div>
          <h1 className="font-serif-display text-3xl md:text-4xl text-[var(--tir-ink)] leading-tight">
            {athlete.name}'s week
          </h1>
          <div className="text-xs text-[var(--tir-muted)] mt-1 max-w-xl">
            All active programs merged into one view. Click any workout to edit it inside its
            source program.
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={goPrev}
            data-testid="calendar-prev-btn"
            className="h-9 w-9 border border-[var(--tir-border)] bg-white hover:border-[var(--tir-ink)] flex items-center justify-center"
            aria-label="Previous week"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            onClick={goToday}
            data-testid="calendar-today-btn"
            className="border border-[var(--tir-ink)] bg-white px-3 h-9 text-[10px] tracking-[0.22em] uppercase font-semibold hover:bg-[var(--tir-ink)] hover:text-white"
          >
            This week
          </button>
          <input
            type="date"
            value={isoDate(anchorDate)}
            onChange={(e) => {
              if (e.target.value) {
                setAnchorDate(mondayOfWeek(new Date(e.target.value + "T00:00:00")));
              }
            }}
            data-testid="calendar-date-input"
            className="border border-[var(--tir-border)] bg-white px-3 h-9 text-sm font-mono-tir focus:outline-none focus:border-[var(--tir-burgundy)]"
          />
          <button
            type="button"
            onClick={goNext}
            data-testid="calendar-next-btn"
            className="h-9 w-9 border border-[var(--tir-border)] bg-white hover:border-[var(--tir-ink)] flex items-center justify-center"
            aria-label="Next week"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </header>

      {/* Active programs strip */}
      <section className="mb-5 flex flex-wrap items-center gap-3">
        <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
          Active programs ({programWeeksInView.size}/{programs.length})
        </div>
        {programs.length === 0 ? (
          <span className="text-xs italic text-[var(--tir-muted)]">
            No programs yet — assign one from the library or build a new one.
          </span>
        ) : (
          programs.map((p) => {
            const inView = programWeeksInView.has(p.id);
            const data = inView ? programWeeksInView.get(p.id) : null;
            return (
              <Link
                key={p.id}
                to={`/coaching/programs/${p.id}/edit${data ? `?week=${data.week.week_number}` : ""}`}
                className={`inline-flex items-center gap-2 border px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                  inView
                    ? "border-[var(--tir-burgundy)] text-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)] hover:text-white"
                    : "border-[var(--tir-border)] text-[var(--tir-muted)] hover:border-[var(--tir-ink)] hover:text-[var(--tir-ink)]"
                }`}
                data-testid={`active-program-chip-${p.id}`}
              >
                <CalendarDays size={11} />
                <span className="truncate max-w-[200px]">{p.title}</span>
                {inView ? (
                  <span className="font-mono-tir text-[10px] opacity-80">
                    W{data.week.week_number}
                  </span>
                ) : (
                  <span className="font-mono-tir text-[10px] opacity-50">—</span>
                )}
              </Link>
            );
          })
        )}
      </section>

      {/* Volume + race banner */}
      <section className="mb-5 flex flex-wrap items-baseline gap-x-8 gap-y-2 border-b border-[var(--tir-border)] pb-3">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
            Combined volume this week
          </div>
          <div className="font-mono-tir text-base text-[var(--tir-burgundy)] mt-0.5">
            {formatVolume(combinedVolume) || "—"}
          </div>
        </div>
        {athletePaces && (
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
              Easy pace
            </div>
            <div className="font-mono-tir text-sm text-[var(--tir-ink)] mt-0.5">
              {Math.round(athletePaces.easy.slow_sec_per_km / 60)}:
              {String(Math.round(athletePaces.easy.slow_sec_per_km) % 60).padStart(2, "0")}
              –{Math.round(athletePaces.easy.fast_sec_per_km / 60)}:
              {String(Math.round(athletePaces.easy.fast_sec_per_km) % 60).padStart(2, "0")}
              /km
            </div>
          </div>
        )}
        {racesInView.length > 0 && (
          <div className="flex items-baseline gap-2 ml-auto">
            <Trophy size={13} className="text-[var(--tir-burgundy)]" />
            {racesInView.map(({ race, dayIdx }) => (
              <span key={race.id} className="text-xs text-[var(--tir-burgundy)] font-semibold">
                {race.name} · {DAY_LABELS[dayIdx]} {fmt(weekDates[dayIdx])}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* Day grid */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="calendar-grid">
          {weekDates.map((d, i) => {
            const isToday = isoDate(d) === isoDate(new Date());
            const dayWorkouts = workoutsByDay[i];
            const raceHere = racesInView.find((r) => r.dayIdx === i);
            return (
              <Droppable droppableId={`day-${i}`} key={i}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`border bg-[var(--tir-ivory)] min-h-[200px] flex flex-col transition-colors ${
                      isToday ? "border-[var(--tir-burgundy)] border-2" : "border-[var(--tir-border)]"
                    } ${raceHere ? "bg-[#fdf6e7]" : ""} ${
                      snapshot.isDraggingOver ? "bg-white border-[var(--tir-burgundy)]" : ""
                    }`}
                    data-testid={`calendar-day-${DAY_LABELS[i]}`}
                  >
                    <div className="px-2.5 py-2 border-b border-[var(--tir-border)] flex items-baseline justify-between">
                      <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-ink)]">
                        {DAY_LABELS[i]}
                      </span>
                      <span className="font-mono-tir text-[10px] text-[var(--tir-muted)]">
                        {fmt(d)}
                      </span>
                    </div>
                    <div className="flex-1 p-2 space-y-1.5">
                      {raceHere && (
                        <div
                          className="border border-[var(--tir-burgundy)] bg-white px-2 py-1 text-[10px] tracking-[0.14em] uppercase font-bold text-[var(--tir-burgundy)] flex items-center gap-1"
                          data-testid={`calendar-race-${raceHere.race.id}`}
                        >
                          <Trophy size={10} /> {raceHere.race.priority || "A"} · {raceHere.race.name}
                        </div>
                      )}
                      {dayWorkouts.length === 0 && !raceHere && !snapshot.isDraggingOver ? (
                        <div className="text-[10px] italic text-[var(--tir-muted)] py-2 text-center">
                          Nothing scheduled
                        </div>
                      ) : null}
                      {dayWorkouts.map((entry, idx) => {
                        const draggableId = `${entry.programId}::${entry.workout.id}`;
                        return (
                          <Draggable key={draggableId} draggableId={draggableId} index={idx}>
                            {(dragProvided, dragSnapshot) => (
                              <div
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                              >
                                <WorkoutChip
                                  workout={entry.workout}
                                  programTitle={entry.programTitle}
                                  programId={entry.programId}
                                  weekNumber={entry.weekNumber}
                                  dragHandleProps={dragProvided.dragHandleProps}
                                  isDragging={dragSnapshot.isDragging}
                                />
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {provided.placeholder}
                    </div>
                  </div>
                )}
              </Droppable>
            );
          })}
        </div>
      </DragDropContext>

      <div className="mt-5 text-[10px] italic text-[var(--tir-muted)] max-w-2xl">
        Each card is colored by workout type and tagged with the source program in burgundy. Click
        a card to jump to that program's week editor — or grab the handle and drag a workout to a
        different day. Moves are saved to the source program automatically.
      </div>
    </div>
  );
}
